import React, { useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { IntlPhoneField } from '@/components/phone/IntlPhoneField';
import { useLanguage } from '../context/LanguageContext';
import { Phone, MapPin, MessageCircle, Send, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import { cn } from '@/lib/utils';
import { getContactFormCopy, defaultCountryFromLanguage } from '@/lib/contactForm/contactFormCopy';
import { buildContactPayload, sanitizeContactDraft } from '@/lib/contactForm/buildPayload';
import { submitContactPayload } from '@/lib/contactForm/submitContact';
import { getWsjCommercialWhatsappUrl } from '@/lib/wsjCommercialWhatsapp';

const FIELD_ORDER = ['name', 'email', 'department', 'phone', 'message'];

const fieldErrorClass =
  'border-red-400/40 !ring-2 !ring-red-500/20 focus-visible:!border-red-400/55 focus-visible:!ring-red-400/25';

const contactLabelClass =
  'mb-2 block text-[0.8125rem] font-medium tracking-[0.06em] text-white/52';

const contactFieldBase =
  'rounded-xl border border-white/[0.09] bg-white/[0.035] text-base sm:text-[15px] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-md transition-all duration-200 placeholder:text-white/26 placeholder:font-light placeholder:tracking-wide hover:border-white/[0.13] hover:bg-white/[0.05] focus-visible:border-amber-500/45 focus-visible:ring-2 focus-visible:ring-amber-400/18 focus-visible:ring-offset-0 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-45';

const contactCardFrame =
  'relative rounded-[1.35rem] bg-gradient-to-br from-white/[0.14] via-white/[0.06] to-white/[0.02] p-px shadow-[0_28px_90px_-32px_rgba(0,0,0,0.72)]';

const contactCardInner =
  'relative overflow-hidden rounded-[1.32rem] bg-slate-950/55 backdrop-blur-2xl';

const Contact = () => {
  const { t, language } = useLanguage();
  const copy = useMemo(() => getContactFormCopy(language), [language]);
  const defaultCountry = useMemo(() => defaultCountryFromLanguage(language), [language]);

  const successCopy = {
    'pt-BR': {
      title: 'Mensagem enviada!',
      subtitle: 'Obrigado pelo contato. Responderemos em breve.'
    },
    'pt-PT': {
      title: 'Mensagem enviada!',
      subtitle: 'Obrigado pelo contacto. Responderemos em breve.'
    },
    en: {
      title: 'Message sent!',
      subtitle: 'Thanks for contacting us. We will reply soon.'
    },
    es: {
      title: '¡Mensaje enviado!',
      subtitle: 'Gracias por contactarnos. Responderemos pronto.'
    },
    fr: {
      title: 'Message envoyé !',
      subtitle: 'Merci pour votre contact. Nous répondrons bientôt.'
    },
    it: {
      title: 'Messaggio inviato!',
      subtitle: 'Grazie per averci contattato. Risponderemo presto.'
    },
    hi: {
      title: 'संदेश भेजा गया!',
      subtitle: 'संपर्क करने के लिए धन्यवाद। हम जल्द जवाब देंगे।'
    },
    de: {
      title: 'Nachricht gesendet!',
      subtitle: 'Vielen Dank für Ihre Nachricht. Wir melden uns bald.'
    },
    nl: {
      title: 'Bericht verzonden!',
      subtitle: 'Bedankt voor uw bericht. We reageren zo snel mogelijk.'
    },
    ru: {
      title: 'Сообщение отправлено!',
      subtitle: 'Спасибо за обращение. Мы скоро ответим.'
    },
    ar: {
      title: 'تم إرسال الرسالة!',
      subtitle: 'شكرًا لتواصلك. سنرد قريبًا.'
    },
    'zh-CN': {
      title: '消息已发送！',
      subtitle: '感谢您的联系，我们会尽快回复。'
    },
    'zh-TW': {
      title: '訊息已送出！',
      subtitle: '感謝您的聯絡，我們會盡快回覆。'
    }
  };

  const activeSuccess = successCopy[language] || successCopy['pt-BR'];

  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .min(2, { message: copy.errors.nameMin })
          .max(120, { message: copy.errors.nameMax }),
        email: z.string().trim().email({ message: copy.errors.email }),
        department: z.enum(['info', 'suporte', 'vendas'], {
          required_error: copy.errors.department,
          invalid_type_error: copy.errors.department
        }),
        phone: z
          .union([z.string(), z.undefined()])
          .transform((v) => (v == null ? '' : String(v).trim()))
          .refine((v) => !v || isValidPhoneNumber(v), { message: copy.errors.phone }),
        message: z
          .string()
          .trim()
          .min(20, { message: copy.errors.messageMin })
          .max(5000, { message: copy.errors.messageMax })
      }),
    [copy]
  );

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setFocus,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      department: undefined,
      phone: '',
      message: ''
    },
    shouldFocusError: false,
    mode: 'onTouched'
  });

  const messageLen = watch('message')?.length ?? 0;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const onInvalid = (errs) => {
    for (const key of FIELD_ORDER) {
      if (errs[key]) {
        setFocus(key);
        break;
      }
    }
  };

  const onSubmit = async (raw) => {
    setSubmitError(null);
    const sanitized = sanitizeContactDraft(raw);

    const payload = buildContactPayload(
      {
        name: sanitized.name,
        email: sanitized.email,
        department: raw.department,
        phone: sanitized.phone,
        message: sanitized.message
      },
      language
    );

    try {
      await submitContactPayload(payload);
      reset({
        name: '',
        email: '',
        department: undefined,
        phone: '',
        message: ''
      });
      setIsSubmitted(true);
      window.setTimeout(() => setIsSubmitted(false), 4000);
    } catch (e) {
      const isNetwork =
        e &&
        (e.name === 'TypeError' ||
          (typeof e.message === 'string' &&
            (e.message.includes('fetch') || e.message.includes('Network'))));
      setSubmitError(isNetwork ? copy.networkError : copy.submitError);
    }
  };

  const handleWhatsApp = () => {
    const name = watch('name')?.trim();
    const message = `Olá! Meu nome é ${name || '[Seu Nome]'}. Gostaria de saber mais sobre os serviços da WSJ Soluções Digitais.`;
    window.open(getWsjCommercialWhatsappUrl(message), '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="contact" className="relative w-full min-w-0 overflow-hidden py-28 sm:py-32">
      <div className="absolute inset-0 tech-grid opacity-[0.18]" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-96 w-96 rounded-full bg-blue-500/5 blur-3xl" />
      <div className="pointer-events-none absolute right-0 top-1/4 h-[min(42rem,90vw)] w-[min(42rem,90vw)] translate-x-0 sm:translate-x-1/4 rounded-full bg-gradient-to-bl from-amber-500/[0.06] via-yellow-500/[0.02] to-transparent blur-[100px]" />

      <div className="container relative z-10 mx-auto box-border w-full max-w-full px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-16 text-center sm:mb-20 lg:mb-24">
            <h2 className="mx-auto mb-5 max-w-4xl text-balance text-4xl font-semibold leading-[1.1] tracking-[-0.035em] text-white sm:mb-6 sm:text-5xl lg:text-[3.05rem]">
              {t.contact.title}
            </h2>
            <p className="mx-auto mb-9 max-w-xl text-base font-light leading-[1.65] tracking-wide text-white/42 sm:mb-10 sm:text-[15px] sm:leading-relaxed">
              {t.contact.subtitle}
            </p>
            <div className="mx-auto h-px w-20 bg-gradient-to-r from-transparent via-amber-400/55 to-transparent sm:w-24" />
          </div>

          <div className="grid items-start gap-12 sm:gap-14 lg:grid-cols-2 lg:gap-16 xl:gap-20">
            <div className={contactCardFrame}>
              <div className={contactCardInner}>
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_90%_50%_at_50%_-15%,rgba(251,191,36,0.06),transparent_58%)]" />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/[0.06] via-transparent to-transparent opacity-95" />
                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/22 to-transparent" />
                <div className="relative px-4 pb-8 pt-8 sm:px-9 sm:pb-9 sm:pt-9">
                  <div className="mb-8">
                    <h3 className="text-3xl font-light leading-tight tracking-[-0.03em] text-white sm:text-[2rem]">
                      {t.contact.formTitle}
                    </h3>
                    <div className="mt-4 h-px max-w-[7.5rem] bg-gradient-to-r from-amber-400/45 via-amber-500/22 to-transparent" />
                  </div>

              {isSubmitted ? (
                <div className="flex flex-col items-center justify-center py-10">
                  <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-amber-400/20 to-amber-600/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.12)] ring-1 ring-amber-500/20">
                    <CheckCircle className="h-10 w-10 text-amber-400/90" />
                  </div>
                  <h4 className="mb-2 text-xl font-medium tracking-tight text-white">
                    {activeSuccess.title}
                  </h4>
                  <p className="max-w-sm text-center text-[15px] font-light leading-relaxed text-white/48">
                    {activeSuccess.subtitle}
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit(onSubmit, onInvalid)}
                  className="space-y-7"
                  noValidate
                >
                  {submitError && (
                    <div
                      role="alert"
                      className="rounded-xl border border-red-400/25 bg-red-500/[0.08] px-4 py-3.5 text-sm leading-relaxed text-red-100/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] backdrop-blur-sm"
                    >
                      {submitError}
                    </div>
                  )}

                  <div>
                    <Label htmlFor="name" className={contactLabelClass}>
                      {t.contact.nameLabel}
                    </Label>
                    <Input
                      id="name"
                      autoComplete="name"
                      aria-invalid={!!errors.name}
                      aria-describedby={errors.name ? 'name-error' : undefined}
                      {...register('name')}
                      placeholder={t.contact.namePlaceholder}
                      className={cn(
                        'mt-2 h-11 px-3.5',
                        contactFieldBase,
                        errors.name && fieldErrorClass
                      )}
                    />
                    {errors.name && (
                      <p id="name-error" className="mt-1.5 text-sm text-red-300/90">
                        {errors.name.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="email" className={contactLabelClass}>
                      {t.contact.emailLabel}
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? 'email-error' : undefined}
                      {...register('email')}
                      placeholder={t.contact.emailPlaceholder}
                      className={cn(
                        'mt-2 h-11 px-3.5',
                        contactFieldBase,
                        errors.email && fieldErrorClass
                      )}
                    />
                    {errors.email && (
                      <p id="email-error" className="mt-1.5 text-sm text-red-300/90">
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="department" className={contactLabelClass}>
                      {copy.departmentLabel}
                    </Label>
                    <Controller
                      name="department"
                      control={control}
                      render={({ field }) => (
                        <Select
                          value={field.value || undefined}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger
                            id="department"
                            aria-invalid={!!errors.department}
                            aria-describedby={errors.department ? 'department-error' : undefined}
                            className={cn(
                              'mt-2 h-11 w-full rounded-xl border border-white/[0.09] bg-white/[0.035] px-3.5 text-base sm:text-[15px] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-md transition-all duration-200 hover:border-white/[0.13] hover:bg-white/[0.05] focus:ring-2 focus:ring-amber-400/18 focus:ring-offset-0 data-[placeholder]:text-white/28',
                              errors.department && fieldErrorClass
                            )}
                          >
                            <SelectValue placeholder={copy.departmentPlaceholder} />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-950 border-white/10 text-white z-[100]">
                            <SelectItem value="info" className="focus:bg-white/10 focus:text-white">
                              {copy.deptInfo}
                            </SelectItem>
                            <SelectItem value="suporte" className="focus:bg-white/10 focus:text-white">
                              {copy.deptSupport}
                            </SelectItem>
                            <SelectItem value="vendas" className="focus:bg-white/10 focus:text-white">
                              {copy.deptSales}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.department && (
                      <p id="department-error" className="mt-1.5 text-sm text-red-300/90">
                        {errors.department.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="phone" className={contactLabelClass}>
                      {copy.phoneLabel}
                    </Label>
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => (
                        <IntlPhoneField
                          key={`phone-field-${language}-${defaultCountry}`}
                          id="phone"
                          defaultCountry={defaultCountry}
                          language={language}
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          aria-invalid={!!errors.phone}
                          aria-describedby={errors.phone ? 'phone-error' : undefined}
                          className="mt-2"
                          hasError={!!errors.phone}
                        />
                      )}
                    />
                    {errors.phone && (
                      <p id="phone-error" className="mt-1.5 text-sm text-red-300/90">
                        {errors.phone.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
                      <Label htmlFor="message" className={`${contactLabelClass} mb-0`}>
                        {t.contact.messageLabel}
                      </Label>
                      <span
                        className="text-[11px] font-medium tabular-nums tracking-[0.08em] text-white/38"
                        aria-live="polite"
                      >
                        {copy.messageCounter(messageLen)}
                      </span>
                    </div>
                    <p className="mt-1.5 text-[11px] font-light tracking-wide text-white/32">
                      {copy.messageMinHint}
                    </p>
                    <Textarea
                      id="message"
                      rows={10}
                      maxLength={5000}
                      autoComplete="off"
                      aria-invalid={!!errors.message}
                      aria-describedby={errors.message ? 'message-error' : undefined}
                      {...register('message')}
                      placeholder={t.contact.messagePlaceholder}
                      className={cn(
                        'mt-2.5 min-h-[200px] resize-y px-3.5 py-3 leading-relaxed',
                        contactFieldBase,
                        errors.message && fieldErrorClass
                      )}
                    />
                    {errors.message && (
                      <p id="message-error" className="mt-1.5 text-sm text-red-300/90">
                        {errors.message.message}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    aria-busy={isSubmitting}
                    className="mt-1 w-full rounded-xl border border-white/15 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 py-6 text-[15px] font-semibold tracking-wide text-slate-950 shadow-[0_12px_36px_-12px_rgba(245,158,11,0.45),inset_0_1px_0_rgba(255,255,255,0.35)] transition-all duration-300 hover:brightness-[1.03] hover:shadow-[0_18px_44px_-14px_rgba(245,158,11,0.5)] active:translate-y-px disabled:pointer-events-none disabled:opacity-55 sm:py-6"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                        {copy.sending}
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 w-5 h-5" />
                        {t.contact.submitButton}
                      </>
                    )}
                  </Button>
                </form>
              )}

              <div className="mt-10 pt-10">
                <div
                  className="mb-8 h-px w-full bg-gradient-to-r from-transparent via-white/[0.11] to-transparent"
                  aria-hidden
                />
                <h4 className="mb-4 text-base font-medium tracking-tight text-white/88">
                  {t.contact.whatsappTitle}
                </h4>
                <Button
                  onClick={handleWhatsApp}
                  size="lg"
                  className="w-full rounded-xl border border-white/10 bg-emerald-600/95 py-6 text-[15px] font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12)] transition-all duration-300 hover:border-white/15 hover:bg-emerald-500 hover:shadow-[0_8px_28px_-8px_rgba(16,185,129,0.45)] active:translate-y-px sm:py-6"
                >
                  <MessageCircle className="mr-2 w-5 h-5" />
                  {t.contact.whatsappButton}
                </Button>
              </div>
                </div>
              </div>
            </div>

            <div className="relative flex flex-col gap-10 lg:gap-12">
              <div className="pointer-events-none absolute -right-8 top-12 hidden h-72 w-72 rounded-full bg-amber-400/[0.06] blur-[80px] lg:block" aria-hidden />

              <div>
                <h3 className="text-3xl font-light leading-tight tracking-[-0.03em] text-white sm:text-[2rem]">
                  {t.contact.infoTitle}
                </h3>
                <div className="mt-4 h-px max-w-[7.5rem] bg-gradient-to-r from-amber-400/45 via-amber-500/22 to-transparent" />
              </div>

              <div className="group/card relative rounded-[1.35rem] bg-gradient-to-br from-white/[0.14] via-white/[0.06] to-white/[0.02] p-[1px] shadow-[0_28px_90px_-32px_rgba(0,0,0,0.75)] transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_40px_100px_-36px_rgba(0,0,0,0.82)]">
                <div className="relative overflow-hidden rounded-[1.32rem] bg-slate-950/55 backdrop-blur-2xl">
                  <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_85%_55%_at_50%_-10%,rgba(251,191,36,0.09),transparent_55%)]" />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/[0.07] via-transparent to-transparent opacity-90" />
                  <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
                  <div className="relative px-4 pb-8 pt-7 sm:px-9 sm:pb-9 sm:pt-8">
                    <div className="flex items-center gap-4">
                      <div className="relative flex h-11 w-11 shrink-0 items-center justify-center">
                        <span className="absolute inset-0 rounded-xl bg-amber-400/25 blur-lg" aria-hidden />
                        <span className="absolute inset-0 rounded-xl bg-gradient-to-br from-amber-300/90 to-amber-800/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] ring-1 ring-white/20" />
                        <Phone className="relative h-[18px] w-[18px] text-slate-950 drop-shadow-sm" aria-hidden />
                      </div>
                      <h4 className="text-lg font-medium tracking-tight text-white/95 sm:text-xl">
                        {t.contact.phonesTitle}
                      </h4>
                    </div>
                    <div
                      className="my-7 h-px w-full bg-gradient-to-r from-transparent via-white/[0.12] to-transparent"
                      aria-hidden
                    />
                    <ul className="space-y-7">
                      <li>
                        <a
                          href="tel:+351924963314"
                          className="group/line block rounded-2xl border border-transparent px-2 py-3 transition-all duration-300 ease-out hover:border-white/[0.07] hover:bg-white/[0.04] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/40"
                        >
                          <div className="text-[0.625rem] font-semibold uppercase tracking-[0.28em] text-white/22">
                            {t.contact.phonePortugalLabel}
                          </div>
                          <div className="relative mt-2.5 inline-block min-w-0 text-xl font-medium tabular-nums tracking-[-0.02em] text-white [font-variant-numeric:tabular-nums] after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-0 after:rounded-full after:bg-gradient-to-r after:from-amber-300/70 after:to-amber-600/40 after:transition-all after:duration-300 group-hover/line:after:w-full sm:text-2xl sm:leading-none">
                            {t.contact.phonePortugalDisplay}
                          </div>
                        </a>
                      </li>
                      <li
                        className="h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent"
                        aria-hidden
                      />
                      <li>
                        <a
                          href="tel:+5518997874643"
                          className="group/line block rounded-2xl border border-transparent px-2 py-3 transition-all duration-300 ease-out hover:border-white/[0.07] hover:bg-white/[0.04] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/40"
                        >
                          <div className="text-[0.625rem] font-semibold uppercase tracking-[0.28em] text-white/22">
                            {t.contact.phoneBrazilLabel}
                          </div>
                          <div className="relative mt-2.5 inline-block min-w-0 text-xl font-medium tabular-nums tracking-[-0.02em] text-white [font-variant-numeric:tabular-nums] after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-0 after:rounded-full after:bg-gradient-to-r after:from-amber-300/70 after:to-amber-600/40 after:transition-all after:duration-300 group-hover/line:after:w-full sm:text-2xl sm:leading-none">
                            {t.contact.phoneBrazilDisplay}
                          </div>
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="group/card relative rounded-[1.35rem] bg-gradient-to-br from-white/[0.14] via-white/[0.06] to-white/[0.02] p-[1px] shadow-[0_28px_90px_-32px_rgba(0,0,0,0.75)] transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_40px_100px_-36px_rgba(0,0,0,0.82)]">
                <div className="relative overflow-hidden rounded-[1.32rem] bg-slate-950/55 backdrop-blur-2xl">
                  <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_85%_55%_at_50%_-10%,rgba(251,191,36,0.07),transparent_55%)]" />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/[0.06] via-transparent to-transparent opacity-90" />
                  <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
                  <div className="relative px-4 pb-8 pt-7 sm:px-9 sm:pb-9 sm:pt-8">
                    <div className="flex items-center gap-4">
                      <div className="relative flex h-11 w-11 shrink-0 items-center justify-center">
                        <span className="absolute inset-0 rounded-xl bg-amber-400/22 blur-lg" aria-hidden />
                        <span className="absolute inset-0 rounded-xl bg-gradient-to-br from-amber-300/90 to-amber-800/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] ring-1 ring-white/20" />
                        <MapPin className="relative h-[18px] w-[18px] text-slate-950 drop-shadow-sm" aria-hidden />
                      </div>
                      <h4 className="text-lg font-medium tracking-tight text-white/95 sm:text-xl">
                        {t.contact.locationsTitle}
                      </h4>
                    </div>
                    <div
                      className="my-7 h-px w-full bg-gradient-to-r from-transparent via-white/[0.12] to-transparent"
                      aria-hidden
                    />
                    <ul className="space-y-3.5">
                      <li className="group/row relative overflow-hidden rounded-xl border border-white/[0.06] bg-gradient-to-r from-white/[0.045] via-white/[0.02] to-transparent px-4 py-4 pl-5 transition-colors duration-300 hover:border-white/[0.1] sm:px-5">
                        <span
                          className="absolute bottom-0 left-0 top-0 w-[3px] bg-gradient-to-b from-amber-400/85 via-amber-500/50 to-amber-800/35"
                          aria-hidden
                        />
                        <div className="flex items-start gap-4 pl-1">
                          <span className="mt-0.5 font-mono text-[10px] font-semibold uppercase tabular-nums tracking-[0.2em] text-amber-500/65">
                            01
                          </span>
                          <span className="text-[15px] font-medium leading-snug tracking-[-0.015em] text-white/[0.92] sm:text-base">
                            {t.contact.locationPortugal}
                          </span>
                        </div>
                      </li>
                      <li className="group/row relative overflow-hidden rounded-xl border border-white/[0.06] bg-gradient-to-r from-white/[0.045] via-white/[0.02] to-transparent px-4 py-4 pl-5 transition-colors duration-300 hover:border-white/[0.1] sm:px-5">
                        <span
                          className="absolute bottom-0 left-0 top-0 w-[3px] bg-gradient-to-b from-amber-400/85 via-amber-500/50 to-amber-800/35"
                          aria-hidden
                        />
                        <div className="flex items-start gap-4 pl-1">
                          <span className="mt-0.5 font-mono text-[10px] font-semibold uppercase tabular-nums tracking-[0.2em] text-amber-500/65">
                            02
                          </span>
                          <span className="text-[15px] font-medium leading-snug tracking-[-0.015em] text-white/[0.92] sm:text-base">
                            {t.contact.locationBrazil}
                          </span>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
